import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import BrewmasterChat from './BrewmasterChat';
import BrewmasterInput from './BrewmasterInput';
import BrewmasterProposalReview from './BrewmasterProposalReview';
import { chatMessage, proposeConversion } from '../../../services/brewmasterService';
import useBrewmasterSession from '../../../hooks/useBrewmasterSession';

const MAX_CONVERSATION_LENGTH = 50;

const WELCOME_MESSAGE = {
  role: 'brewmaster',
  content: "Welcome to the Workshop! I'm the Brewmaster, your Daggerheart rules companion. Ask me anything about the SRD, drop a character sheet PDF to convert, or describe a concept and I'll help you build it.",
};

function BrewmasterTab({ userId, onCharacterCreated }) {
  const navigate = useNavigate();
  const { loadSession, loadFromFirestore, saveSession, clearSession, cleanup } = useBrewmasterSession(userId);
  const restoredRef = useRef(undefined);

  // Tier 1: Synchronous hydration from sessionStorage
  if (restoredRef.current === undefined) {
    restoredRef.current = loadSession();
  }
  const restored = restoredRef.current;

  const [messages, setMessages] = useState(() => restored?.messages || [WELCOME_MESSAGE]);
  const [analyzing, setAnalyzing] = useState(false);
  const [proposal, setProposal] = useState(() => restored?.proposal || null);
  const [parsedCharacter, setParsedCharacter] = useState(() => restored?.parsedCharacter || null);
  const [building, setBuilding] = useState(false);

  // Tier 1 restore toast
  useEffect(() => {
    if (restoredRef.current) {
      const ago = Math.round((Date.now() - restoredRef.current.restoredAt) / 60000);
      toast.success(
        ago > 0
          ? `Restored conversation from ${ago}m ago`
          : 'Restored previous conversation',
        { icon: '\uD83D\uDD04', duration: 3000 }
      );
      restoredRef.current = null;
    }
  }, []);

  // Tier 2: Async fallback from Firestore (when sessionStorage is empty)
  useEffect(() => {
    if (restored || !userId) return;

    let cancelled = false;
    loadFromFirestore().then((session) => {
      if (cancelled || !session) return;
      setMessages(session.messages);
      setProposal(session.proposal);
      setParsedCharacter(session.parsedCharacter);
      const ago = Math.round((Date.now() - session.restoredAt) / 60000);
      toast.success(
        ago > 0
          ? `Restored conversation from ${ago}m ago`
          : 'Restored previous conversation',
        { icon: '\uD83D\uDD04', duration: 3000 }
      );
    });
    return () => { cancelled = true; };
  }, [restored, userId, loadFromFirestore]);

  useEffect(() => {
    saveSession({ messages, proposal, parsedCharacter });
  }, [messages, proposal, parsedCharacter, saveSession]);

  // Cancel pending debounced writes on unmount
  useEffect(() => cleanup, [cleanup]);

  const addMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  /**
   * Build conversation history for the Cloud Function.
   * Maps 'brewmaster' role to 'model' for Gemini, skips the welcome message.
   */
  const buildHistory = useCallback((currentMessages) => {
    return currentMessages
      .filter((m) => m.content !== WELCOME_MESSAGE.content)
      .map((m) => ({
        role: m.role === 'brewmaster' ? 'model' : 'user',
        content: m.content,
      }));
  }, []);

  // Ref tracks latest proposal so async callbacks always see current value
  const proposalRef = useRef(proposal);
  useEffect(() => { proposalRef.current = proposal; }, [proposal]);

  /**
   * Core chat handler. Sends a message (with optional PDF attachment)
   * to the Brewmaster Cloud Function and processes the response.
   */
  const sendToBrewmaster = useCallback(async (userMessage, attachments) => {
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setAnalyzing(true);

    try {
      // Check conversation length
      if (newMessages.length >= MAX_CONVERSATION_LENGTH) {
        addMessage({
          role: 'brewmaster',
          content: "We've been at this a while. Want to start a new conversation? Hit 'New conversation' above.",
        });
        return;
      }

      const history = buildHistory(newMessages);
      const result = await chatMessage(userId, history, attachments);

      // Add Brewmaster response
      if (result.response) {
        addMessage({ role: 'brewmaster', content: result.response });
      }

      // Handle structured data from tool calls
      if (result.proposal) {
        setProposal(result.proposal);
        proposalRef.current = result.proposal;
        if (result.proposal.parsedCharacter) {
          setParsedCharacter(result.proposal.parsedCharacter);
        }
      }

      // Handle character creation signal — use ref to avoid stale closure
      const currentProposal = result.proposal || proposalRef.current;
      if (result.toolsUsed?.includes('createCharacter') && currentProposal) {
        try {
          sessionStorage.setItem('chag_conversion_draft', JSON.stringify(currentProposal));
        } catch {
          toast.error('Could not save proposal. Try again.');
          return;
        }
        addMessage({
          role: 'brewmaster',
          content: 'Taking you to the character builder with everything pre-filled. Review and hit Forge when it looks right.',
        });
        navigate('/characters/new/build');
      }
    } catch (err) {
      const msg = err.code === 'functions/resource-exhausted'
        ? "You've hit the daily chat limit. Come back tomorrow."
        : err.code === 'functions/unauthenticated'
          ? 'You need to be signed in to chat with the Brewmaster.'
          : 'Something went wrong. Give it another try.';
      addMessage({ role: 'brewmaster', content: msg });
    } finally {
      setAnalyzing(false);
    }
  }, [messages, userId, addMessage, buildHistory, navigate]);

  const handleSendMessage = useCallback(async (text) => {
    await sendToBrewmaster({ role: 'user', content: text });
  }, [sendToBrewmaster]);

  const handleUploadPDF = useCallback(async (pdfBase64, fileName) => {
    addMessage({ role: 'user', content: `Uploaded: ${fileName}` });
    addMessage({ role: 'brewmaster', content: 'Let me take a look at this character sheet...' });
    setAnalyzing(true);

    try {
      // Use the existing Stage 1+2 pipeline for PDF parsing (needs 120s timeout)
      const result = await proposeConversion(userId, pdfBase64, (stage) => {
        if (stage === 'mapping') {
          addMessage({ role: 'brewmaster', content: 'Got the basics. Now mapping to Daggerheart...' });
        }
      });

      setParsedCharacter(result.parsedCharacter);
      setProposal(result.proposal);

      // Tell the Brewmaster about the result so it has context for follow-up chat
      addMessage({
        role: 'brewmaster',
        content: `Here's what I'd suggest for ${result.proposal.characterName}. Review each section below, or just tell me what you'd like changed.`,
      });
    } catch (err) {
      const msg = err.code === 'functions/resource-exhausted'
        ? "You've hit the daily conversion limit. Try again later."
        : err.code === 'functions/invalid-argument'
          ? "I couldn't read this as a character sheet. Make sure it's a valid PDF export."
          : 'Something went wrong analyzing that PDF. Give it another try.';
      addMessage({ role: 'brewmaster', content: msg });
    } finally {
      setAnalyzing(false);
    }
  }, [userId, addMessage]);

  const handleCreateCharacter = useCallback(() => {
    if (!proposal) return;

    // Store the proposal for the character builder to pick up
    try {
      sessionStorage.setItem('chag_conversion_draft', JSON.stringify(proposal));
    } catch {
      toast.error('Could not save proposal. Try again.');
      return;
    }

    addMessage({
      role: 'brewmaster',
      content: `Taking you to the character builder with everything pre-filled. Review and hit Forge when it looks right.`,
    });

    // Navigate to the builder — the character gets created when the user
    // clicks Forge in the builder UI, not here.
    navigate('/characters/new/build');
  }, [proposal, addMessage, navigate]);

  /**
   * Handle section changes from the proposal review UI.
   * Sends the change request as a chat message so the Brewmaster revises it.
   */
  const handleSectionChange = useCallback(async (section, instructions) => {
    await handleSendMessage(`For the ${section} section: ${instructions}`);
  }, [handleSendMessage]);

  const handleNewConversation = useCallback(() => {
    clearSession();
    setMessages([WELCOME_MESSAGE]);
    setProposal(null);
    setParsedCharacter(null);
  }, [clearSession]);

  const hasConversation = messages.length > 1 || proposal;
  const busy = analyzing || building;

  return (
    <div className="flex flex-col bg-gray-900/50 rounded-lg border border-gray-700">
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-amber-400">Brewmaster</h3>
          <p className="text-xs text-gray-500">Daggerheart rules companion</p>
        </div>
        {hasConversation && !busy && (
          <button
            onClick={handleNewConversation}
            className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            New conversation
          </button>
        )}
      </div>

      <BrewmasterChat messages={messages}>
        {analyzing && (
          <div className="flex items-center gap-2 text-sm text-gray-400 px-4 py-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500" />
            Brewing...
          </div>
        )}

        {/* Proposal renders inline in the chat flow */}
        {proposal && !building && (
          <BrewmasterProposalReview
            proposal={proposal}
            parsedCharacter={parsedCharacter}
            userId={userId}
            onProposalUpdate={setProposal}
            onConfirm={handleCreateCharacter}
            onSectionChange={handleSectionChange}
          />
        )}
      </BrewmasterChat>

      <BrewmasterInput
        onSendMessage={handleSendMessage}
        onUploadPDF={handleUploadPDF}
        disabled={busy}
      />
    </div>
  );
}

export default BrewmasterTab;
